

















































































































import { Component, Prop, PropSync, Vue, Watch } from "vue-property-decorator";
import DeferredAutocompleteWrapper from "@/components/misc/DeferredAutocompleteWrapper.vue";
import { Action, Getter } from "vuex-class";
import { Country } from "@/shared/models/country";
import { DefferedAutocompleteItem } from "@/components/misc/DeferredAutocomplete.vue";
import { ListModel } from "@/shared/models/listModel";
import { CustomerModel } from "@/shared/models/vCustomer";
import CustomerForm from "@/components/CustomerForm.vue";
import LoadingOverlay from "@/components/misc/LoadingOverlay.vue";

export type PropertyOwnerInfo = {
  id: number; // the important (payload) bit
  name: string | null;
  oib: string | null;

  address: string | null;
  postalCode: string | null;
  city: string | null;

  countryId: number | null;
  countryName: string | null;
  email: string | null;

  ownerPaysVAT: boolean;
};

@Component({
  components: {
    DeferredAutocompleteWrapper,
    CustomerForm,
    LoadingOverlay,
  },
})
export default class PropertyOwner extends Vue {
  @Action("codebooks/refreshCountries") refreshCountries?: () => Promise<void>;
  @Getter("codebooks/countries") countries?: Country[];

  @PropSync("isActive") internalActive?: boolean;

  async created() {
    if ((this.countries?.length ?? 0) === 0 && this.refreshCountries) {
      await this.refreshCountries();
    }
  }

  get sortedCountries() {
    return this.countries
      ?.map((x) => ({
        id: x.id,
        text: this.$t(`Country.Official.${x.isoThreeLetterCode}`).toString(),
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  @Prop({ default: null }) value?: number | null;
  @Prop({ default: false }) showCustomerData?: boolean;
  @Prop({ default: false }) isForOwner?: boolean;
  @Prop({ default: false }) isForPropertyManager?: boolean;

  @Prop() labelType: string | undefined;

  customerId: number | null = null;
  formCustomerId: number | null = null;

  // sets the new customer info when a new is created
  // or the old if he was update or the customer form was just closed
  // the 'value' watcher triggers the load
  setCustomerAndLoad(customerId: number | null) {
    if (customerId || this.formCustomerId) {
      this.customerId = customerId ? customerId : this.formCustomerId;
    }
  }

  // by setting the current customer to null we prepare the autocomplete wrapper
  // to load the new customer info when we set him
  openCustomerForm() {
    this.formCustomerId = this.customerId;
    this.customerFormOpen = true;
    this.customerId = null;
  }

  searchValue = "";
  get customerFormPrefilData() {
    const names = (this.searchValue ?? "").split(" ");
    return {
      firstName: names.splice(0, 1)[0] || "",
      lastName: names.join(" "),
    } as CustomerModel;
  }

  onBlur() {
    if (this.searchValue) {
      this.$nextTick(() => {
        if (!this.customerId) {
          this.openCustomerForm();
        }
      });
    }
  }

  get computedActive() {
    return !!this.searchValue && !this.customerId;
  }
  @Watch("computedActive")
  computedActiveWatch(fresh: boolean) {
    this.internalActive = fresh;
  }

  @Watch("value", { immediate: true })
  valueWatch(fresh: number | null) {
    this.customerId = fresh ?? null;

    if (this.customerId) {
      this.loadCustomerData();
    }
  }

  @Watch("customerId")
  customerIdWatch(fresh: number | null) {
    this.$emit("input", fresh);
  }

  get labelOwnerManager() {
    if (this.labelType == "propertyOwner") {
      return this.$t("Property.Owner");
    }
    if (this.labelType == "propertyManager") {
      return this.$t("Property.PropertyManager");
    }
    return "";
  }

  updateCustomerVATStatusLoading = false;
  updateCustomerVATStatus() {
    const data = {
      id: this.clientInfoData?.id,
      customerVATStatus: this.clientInfoData?.ownerPaysVAT,
    };

    this.updateCustomerVATStatusLoading = true;

    this.axios
      .put(`customers/UpdateCustomerVATStatus`, data)
      .then(() => {
        this.$store.dispatch("showNotification", {
          color: "success",
          message: this.$t("OwnerVATStatus.SaveSuccess"),
          timeout: 5000,
        });
      })
      .catch(() => {
        this.$store.dispatch("showNotification", {
          color: "error",
          message: this.$t("OwnerVATStatus.SaveFailure"),
          timeout: 5000,
        });

        // reset the field in case of an error
        if (this.clientInfoData) {
          this.clientInfoData.ownerPaysVAT = !this.clientInfoData.ownerPaysVAT;
        }
      })
      .finally(() => (this.updateCustomerVATStatusLoading = false));
  }

  customerFormOpen = false;
  clientInfoData: PropertyOwnerInfo | null = null;
  loading = false;
  loadCustomerData() {
    if (this.customerId && !this.loading) {
      this.loading = true;
      this.axios
        .get<CustomerModel>(`customers/${this.customerId}`)
        .then(({ data }) => {
          this.clientInfoData = {
            id: data.id,
            email: data.eMail ?? "",
            name: data.isLegalEntity
              ? data.legalEntityName ?? ""
              : [data.firstName, data.lastName]
                  .filter((x) => !!x && x.length > 0)
                  .join(" "),
            oib: data.isLegalEntity
              ? data.businessId ?? ""
              : data.identificationDocumentNumber ?? "",
            address: [data.streetName, data.houseNumber]
              .filter((x) => !!x && x.length > 0)
              .join(" "),
            postalCode: data.postalCode ?? "",
            city: data.city ?? "",
            countryId: data.countryOfResidenceId ?? null,
            countryName: null,
            ownerPaysVAT: data.ownerPaysVAT,
          };
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  // autocomplete
  isById(x: any): x is { id: number } {
    return x && (x as { id: number }).id !== undefined;
  }
  isBySearch(x: any): x is { search: string } {
    return x && !!(x as { search: string }).search;
  }
  customerFetch(
    val: { id: number } | { search: string }
  ): Promise<DefferedAutocompleteItem[]> {
    if (this.isById(val)) {
      return this.axios.get<any>(`customers/${val.id}`).then(({ data }) => [
        {
          id: data.id,
          text: data.isLegalEntity
            ? data.legalEntityName
            : // prettier-ignore
              `${data.firstName}${data.middleName ? ' '+ data.middleName : ''} ${data.lastName}`,
        },
      ]);
    } else if (this.isBySearch(val)) {
      return this.axios
        .get<ListModel<any>>("customers", {
          params: {
            $orderby: "name",
            name: val.search,
          },
        })
        .then(({ data }) =>
          data.result.map((x) => ({
            id: x.id,
            text: x.name,
          }))
        );
    } else {
      return Promise.resolve([]);
    }
  }
}
